import { useEffect, useRef } from "react";
import { ThicknessBottle } from "../types";
import { trackAnalyticsEvent } from "../analytics";
import { toAppFileUrl } from "../utils/fileUrls";
import { buildShowAssetUrl, isShowMode } from "../showMode";

type Props = {
  bottles: ThicknessBottle[];
  nasMountPath?: string;
  cacheBustKey?: number;
  onUnselect: (id: number) => void;
  onViewDetail: (bottle: ThicknessBottle) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
};

function buildImagePath(
  nasMountPath: string | undefined,
  bottle: ThicknessBottle,
  cacheBustKey?: number
) {
  const trialName = bottle.trial_name || "unknown";
  const relativePath = `thickness_plotter/${trialName}/output/bottle${bottle.relative_bottle_id}_thickness_heatmap.png`;
  let baseUrl = "";
  if (isShowMode) {
    baseUrl = buildShowAssetUrl(relativePath);
  } else if (nasMountPath) {
    const cleanedMount = nasMountPath.replace(/\\/g, "/").replace(/\/$/, "");
    baseUrl = toAppFileUrl(`${cleanedMount}/${relativePath}`);
  }
  const payload =
    bottle.analysis_payload && typeof bottle.analysis_payload === "object"
      ? (bottle.analysis_payload as Record<string, unknown>)
      : null;
  const rawPointsRef =
    payload?.raw_points_ref && typeof payload.raw_points_ref === "object"
      ? (payload.raw_points_ref as Record<string, unknown>)
      : null;
  const analyzerMeta =
    payload?.analyzer_meta && typeof payload.analyzer_meta === "object"
      ? (payload.analyzer_meta as Record<string, unknown>)
      : null;

  const parts: string[] = [];
  if (typeof rawPointsRef?.quick_fingerprint === "string" && rawPointsRef.quick_fingerprint) {
    parts.push(rawPointsRef.quick_fingerprint);
  }
  if (typeof analyzerMeta?.git_commit_hash === "string" && analyzerMeta.git_commit_hash) {
    parts.push(analyzerMeta.git_commit_hash);
  }
  if (typeof cacheBustKey === "number" && Number.isFinite(cacheBustKey) && cacheBustKey > 0) {
    parts.push(String(cacheBustKey));
  }
  if (!parts.length) return baseUrl;

  return `${baseUrl}?v=${encodeURIComponent(parts.join("-"))}`;
}

export default function ThicknessGallery({
  bottles,
  nasMountPath,
  cacheBustKey,
  onUnselect,
  onViewDetail,
  page,
  pageSize,
  onPageChange
}: Props) {
  const trackedOpenRef = useRef(false);
  const totalPages = Math.max(1, Math.ceil(bottles.length / pageSize));
  const start = page * pageSize;
  const paginated = bottles.slice(start, start + pageSize);

  useEffect(() => {
    if (!isShowMode || trackedOpenRef.current || bottles.length === 0) return;
    trackedOpenRef.current = true;
    trackAnalyticsEvent("gallery_opened");
  }, [bottles.length]);

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 className="section-title">Gallery</h3>
        <span className="pill">
          {isShowMode ? "Assets: show export" : `NAS: ${nasMountPath || "not mounted"}`}
        </span>
      </div>
      <div className="responsive-gallery-grid">
        {paginated.map((bottle) => {
          const imgPath = buildImagePath(nasMountPath, bottle, cacheBustKey);
          return (
            <div
              key={bottle.id}
              className="card"
              style={{ background: "var(--surface-elevated)" }}
            >
              <div style={{ fontSize: 12, color: "var(--text-subtle)" }}>
                {bottle.trial_name} (Bottle {bottle.relative_bottle_id})
              </div>
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  marginTop: 6
                }}
              >
                {imgPath ? (
                  <img
                    src={imgPath}
                    alt={bottle.trial_name}
                    style={{
                      width: "min(160px, 100%)",
                      aspectRatio: "1 / 1",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: 8
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: 120,
                      width: "min(160px, 100%)",
                      display: "grid",
                      placeItems: "center",
                      background: "#0c1016",
                      border: "1px dashed #1e2b3d",
                      borderRadius: 8
                    }}
                  >
                    {isShowMode ? "No preview" : "NAS not mounted"}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 10,
                  flexWrap: "wrap",
                  justifyContent: "center"
                }}
              >
                <button
                  className="icon-button"
                  title="View Detail"
                  onClick={() => {
                    trackAnalyticsEvent("plot_view_opened");
                    onViewDetail(bottle);
                  }}
                  aria-label="View Detail"
                >
                  🔍
                </button>
                {!isShowMode && (
                  <button
                    className="icon-button"
                    title="Unselect"
                    onClick={() => onUnselect(bottle.id)}
                    aria-label="Unselect"
                  >
                    ✖
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!bottles.length && <div style={{ color: "var(--text-faint)" }}>No selections to display</div>}
      </div>
      {bottles.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button
            className="button secondary"
            disabled={page === 0}
            onClick={() => onPageChange(Math.max(0, page - 1))}
          >
            Prev
          </button>
          <div className="pill">
            Page {page + 1} / {totalPages}
          </div>
          <button
            className="button secondary"
            disabled={page + 1 >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
